import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../Components/ui/select'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { get, post } from '../../http/restservice'
import { getUserDetails } from '../../utils/AuthUtil'
import env from '../../env/env'
import {
  CHAPTERS,
  applicableAnnualCosts,
  createPlanner,
  newItem,
  hasValue,
  money,
  lifestyleEstimate,
  retirementStreams,
  restorePlanner,
  cleanPlanner,
  ageFromDob,
  childEducationRemainingStages,
  childPlanWithDefaults,
} from './questionnaire2Model'
import './questionnaire2.css'
import QuestionnaireCelebration from './QuestionnaireCelebration'
import QuestionnaireEstimate from './QuestionnaireEstimate'

const API_PATH = 'customerservice/api/firstIdeas'
const LOCAL_DRAFT_KEY = 'fabits-first-ideas-planner-v3'
const icons = require.context('../../assets/FirstIdeas/questionnaire2/cards', false, /\.png$/)
const icon = (name) => {
  const asset = icons(`./${name}.png`)
  return asset.default || asset
}
const assetTypes = [
  'Home',
  'Land',
  'Gold assets',
  'Insurance investment',
  'PF',
  'PPF',
  'Sukanya Samriddhi',
  'Senior Citizen Savings Scheme',
  'FDs / RDs',
  'Post Office Savings',
  'Sovereign Gold Bonds',
  'REC Bonds',
  'Government Bonds',
  'Cash',
  'Digital gold / coins',
  'Gratuity + leave',
  'Mutual funds',
  'Stocks',
  'NPS',
  'RSUs',
  'US stocks (in INR)',
  'Private equity',
]
const incomeTypes = [
  'Salary',
  'Business / professional income',
  'Rental income',
  'Interest income',
  'Dividends',
  'Pension',
  'Capital gains',
  'Other income',
]
const collectionNames = {
  members: 'family',
  salaries: 'income',
  holdings: 'holdings',
  loans: 'loans',
  assetGoals: 'goals',
  extraYearlyExpenses: 'annual costs',
}
const itemNames = {
  members: 'person',
  salaries: 'income source',
  holdings: 'holding',
  loans: 'loan',
  assetGoals: 'goal',
  extraYearlyExpenses: 'annual cost',
}

const collectionAmounts = {
  salaries: { key: 'gross', unit: '/ month', caption: 'Before deductions' },
  extraYearlyExpenses: { key: 'amount', unit: '/ year', caption: 'Annual cost' },
  holdings: { key: 'value', caption: 'Current value' },
  loans: { key: 'amount', caption: 'Outstanding balance' },
  assetGoals: { key: 'amount', caption: 'Estimated cost today' },
}
function CollectionAmount({ section, item }) {
  const format = collectionAmounts[section]
  if (!format) return null
  const value = item[format.key]
  return (
    <div className='q2-income-amount'>
      <p>
        <span>{hasValue(value) ? money(value) : 'Amount not added'}</span>
        {hasValue(value) && format.unit && <small>{format.unit}</small>}
      </p>
      {hasValue(value) && <span className='q2-income-caption'>{format.caption}</span>}
    </div>
  )
}

function Field({ label, value = '', onChange, type = 'text', unit, hint, required, options }) {
  const [focused, setFocused] = useState(false)
  const fieldId = useId()
  if (options)
    return (
      <div className='q2-field'>
        <label id={`${fieldId}-label`} htmlFor={fieldId}>
          {label}
          {required && <span className='q2-required'>Required</span>}
        </label>
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger
            id={fieldId}
            className='q2-select-trigger'
            aria-labelledby={`${fieldId}-label`}
            aria-describedby={hint ? `${fieldId}-hint` : undefined}
            aria-required={required}
          >
            <SelectValue placeholder='Choose an option' />
          </SelectTrigger>
          <SelectContent
            className='q2-select-menu'
            position='popper'
            sideOffset={6}
            collisionPadding={16}
          >
            {[...new Set(options)].map((option) => (
              <SelectItem className='q2-select-option' key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hint && (
          <small id={`${fieldId}-hint`} className='q2-hint'>
            {hint}
          </small>
        )}
      </div>
    )
  const numeric = type === 'money' || type === 'year' || type === 'number'
  const display =
    type === 'money' && !focused && hasValue(value)
      ? Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })
      : value
  return (
    <label className='q2-field'>
      <span>
        {label}
        {required && <span className='q2-required'>Required</span>}
      </span>
      <span className={`q2-input-wrap ${type === 'money' ? 'q2-money' : ''}`}>
        {type === 'money' && <span aria-hidden='true'>₹</span>}
        <input
          type={numeric ? 'text' : type}
          inputMode={numeric ? 'decimal' : undefined}
          value={display ?? ''}
          aria-required={required}
          placeholder={type === 'year' ? 'e.g. 2032' : numeric ? '0' : undefined}
          max={type === 'date' ? new Date().toISOString().slice(0, 10) : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const raw = e.target.value
            onChange(numeric ? raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1') : raw)
          }}
        />
        {unit && <small>{unit}</small>}
      </span>
      {hint && <small className='q2-hint'>{hint}</small>}
    </label>
  )
}
function Options({ label, value, onChange, options = ['Yes', 'No', 'Not sure'], required }) {
  return (
    <fieldset className='q2-options'>
      <legend>
        {label}
        {required && <span className='q2-required'>Required</span>}
      </legend>
      <div>
        {options.map((option) => (
          <button
            type='button'
            key={option}
            aria-pressed={value === option}
            className={value === option ? 'is-selected' : ''}
            onClick={() => onChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

export default function FinancialPlannerQuestionnaire2({
  customerProfile,
  returnPath = '/dashboard',
}) {
  const navigate = useNavigate()
  const [planner, setPlanner] = useState(createPlanner)
  const [profile, setProfile] = useState({})
  const [loadFailed, setLoadFailed] = useState(false)
  const [stepId, setStepId] = useState('welcome')
  const [motion, setMotion] = useState({ direction: 'forward', fromIntro: false })
  const [transitioning, setTransitioning] = useState(false)
  const panelRef = useRef(null)
  const transitionLock = useRef(false)
  const [hydrated, setHydrated] = useState(false)
  const [saving, setSaving] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [celebrate, setCelebrate] = useState(false)
  const [showEstimate, setShowEstimate] = useState(false)
  const [estimateOrigin, setEstimateOrigin] = useState(null)
  const [error, setError] = useState('')
  const [savedLabel, setSavedLabel] = useState('')
  const [editing, setEditing] = useState(null)
  const [removeId, setRemoveId] = useState('')
  const heading = useRef(null)
  const saveLock = useRef(false)
  const savedSignature = useRef('')
  const isLocal = !customerProfile && window.location.hostname === 'localhost'
  const firstName = (
    profile.firstName ||
    profile.name?.split(' ')[0] ||
    profile.first_name ||
    'there'
  ).trim()
  const phone = profile.phoneNumber || profile.phone_number
  const setObject = (section, key, value) =>
    setPlanner((p) => ({ ...p, [section]: { ...p[section], [key]: value } }))
  const setItem = (section, id, key, value) =>
    setPlanner((p) => ({
      ...p,
      [section]: p[section].map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    }))
  const setChildPlan = (childId, updater) =>
    setPlanner((p) => {
      const child = p.members.find((member) => member.id === childId)
      const currentPlan = childPlanWithDefaults(
        p.childPlans?.[childId],
        childId,
        p,
        ageFromDob(child?.dob),
      )
      const nextPlan =
        typeof updater === 'function' ? updater(currentPlan, p) : { ...currentPlan, ...updater }
      return { ...p, childPlans: { ...(p.childPlans || {}), [childId]: nextPlan } }
    })
  const personName = (member) =>
    member.relationship === 'Self' ? member.name || 'You' : member.name || 'This person'
  const validAmount = (value) =>
    hasValue(value) && Number.isFinite(Number(value)) && Number(value) >= 0
  const amountError = (fields) =>
    fields.some(([value]) => hasValue(value) && !validAmount(value))
      ? 'Enter valid amounts of zero or more.'
      : ''
  const isMeaningfulItem = (section, item) => {
    if (item.relationship === 'Self') return false
    if (section === 'loans')
      return hasValue(item.amount) || hasValue(item.emi) || hasValue(item.monthsLeft)
    return true
  }
  const field = (section, key, label, props = {}) => (
    <Field
      label={label}
      value={planner[section][key]}
      onChange={(value) => setObject(section, key, value)}
      {...props}
    />
  )
  const itemField = (section, item, key, label, props = {}) => (
    <Field
      label={label}
      value={section === 'salaries' && key === 'member' ? item[key] || 'You' : item[key]}
      onChange={(value) => setItem(section, item.id, key, value)}
      {...props}
    />
  )
  const moneyRow = (label, value, unit = '') => [
    label,
    hasValue(value) ? `${money(value)}${unit}` : 'Not answered',
  ]
  const annualCosts = applicableAnnualCosts(planner)
  const selectedAnnualCosts = annualCosts.filter(([key]) => planner.annualSelected.includes(key))
  const retirementModel = retirementStreams(planner)
  const activeLoans = planner.loans.filter((loan) => isMeaningfulItem('loans', loan))
  const raw = []
  const card = (data) => raw.push(data)
  function addItem(section, returnTo = stepId) {
    const item = newItem(section)
    transitionTo(`${section}-${item.id}`, 'forward', () => {
      setEditing({ before: planner, returnTo, adding: true, parent: editing })
      setPlanner((p) => ({ ...p, [section]: [...p[section], item] }))
    })
  }
  function editItem(id, returnTo = stepId) {
    transitionTo(id, 'forward', () => setEditing({ before: planner, returnTo, parent: editing }))
  }
  const itemTitle = (section, item) =>
    section === 'members'
      ? personName(item)
      : section === 'salaries'
      ? `${item.incomeType} · ${item.member || 'You'}`
      : section === 'loans'
      ? item.type
      : item.name || itemNames[section]
  function collection(section, chapter, title, note, illustration, extraContent) {
    card({
      id: `add-${section}`,
      chapter,
      title,
      note,
      illustration,
      collection: true,
      collectionSection: section,
      hasItems: planner[section].some((item) => isMeaningfulItem(section, item)),
      rows: [
        [
          collectionNames[section],
          planner[section]
            .filter((item) => isMeaningfulItem(section, item))
            .map((item) => itemTitle(section, item))
            .join(', ') || 'None added',
        ],
      ],
      render: () => (
        <div className='q2-collection'>
          {planner[section]
            .filter((item) => isMeaningfulItem(section, item))
            .map((item) => (
              <div
                className={`q2-item ${collectionAmounts[section] ? 'q2-income-item' : ''}`}
                key={item.id}
              >
                <div className='q2-item-head'>
                  <strong>{itemTitle(section, item)}</strong>
                  <span className='q2-item-actions'>
                    <button
                      type='button'
                      className='q2-item-edit'
                      onClick={() => editItem(`${section}-${item.id}`)}
                    >
                      Edit
                    </button>
                    <button
                      type='button'
                      className='q2-icon-button q2-remove-icon'
                      aria-label={`Remove ${itemNames[section]}`}
                      title={`Remove ${itemNames[section]}`}
                      onClick={() => setRemoveId(item.id)}
                    >
                      <DeleteOutlineIcon fontSize='small' aria-hidden='true' />
                    </button>
                  </span>
                </div>
                <CollectionAmount section={section} item={item} />
                {removeId === item.id ? (
                  <div className='q2-remove-confirm'>
                    <span>Remove this {itemNames[section]} and its answers?</span>
                    <button
                      type='button'
                      onClick={() => {
                        setPlanner((p) => ({
                          ...p,
                          [section]: p[section].filter((row) => row.id !== item.id),
                        }))
                        setRemoveId('')
                      }}
                    >
                      Remove
                    </button>
                    <button type='button' onClick={() => setRemoveId('')}>
                      Keep it
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          {extraContent}
          <button type='button' className='q2-add' onClick={() => addItem(section)}>
            ＋ Add{' '}
            {planner[section].filter((item) => isMeaningfulItem(section, item)).length
              ? 'another '
              : ['salaries', 'extraYearlyExpenses'].includes(section)
              ? 'an '
              : 'a '}
            {itemNames[section]}
          </button>
          {!planner[section].filter((item) => isMeaningfulItem(section, item)).length && (
            <p className='q2-hint'>
              Nothing to add? Choose “Done with {collectionNames[section]}”.
            </p>
          )}
        </div>
      ),
      action: `Done with ${collectionNames[section]}`,
    })
  }

  for (const [index, member] of planner.members.entries()) {
    card({
      id: `members-${member.id}`,
      chapter: 'people',
      title: index === 0 ? `A little about you, ${firstName}.` : 'Who should your plan include?',
      illustration: 'profile-context',
      required: true,
      note:
        index === 0
          ? 'Your age helps set the planning horizon. Tell us what you do today.'
          : 'Include this person if you share finances or help provide for them.',
      rows: [
        ['Name', personName(member)],
        ['Relationship', member.relationship],
        ['Date of birth', member.dob],
        ['Occupation', member.occupation],
        ...(index ? [['Financially supported by you', member.dependent]] : []),
      ],
      validate: () =>
        !member.relationship || (index > 0 && !member.name.trim())
          ? 'Add their name and relationship.'
          : !member.dob || member.dob > new Date().toISOString().slice(0, 10)
          ? 'Add a valid date of birth that is not in the future.'
          : index === 0 && !member.occupation.trim()
          ? 'Add your occupation to continue.'
          : '',
      render: () => (
        <>
          {index > 0 && (
            <>
              {itemField('members', member, 'name', 'Name', { required: true })}
              {itemField('members', member, 'relationship', 'Relationship to you', {
                required: true,
                options: [
                  'Spouse / Partner',
                  'Child',
                  'Parent',
                  'Partner’s parent',
                  'Sibling',
                  'Other dependent',
                ],
              })}
            </>
          )}
          {itemField('members', member, 'dob', 'Date of birth', { type: 'date', required: true })}
          {member.relationship !== 'Child' &&
            itemField('members', member, 'occupation', 'Occupation', {
              required: index === 0,
              hint: 'For example: salaried, self-employed, retired or homemaker.',
            })}
          {index > 0 && (
            <Options
              label='Do you financially support this person?'
              value={member.dependent}
              onChange={(v) => setItem('members', member.id, 'dependent', v)}
            />
          )}
        </>
      ),
    })
  }
  collection(
    'members',
    'people',
    'Who else is part of your plan?',
    'Your partner, children and anyone you financially support. Include parents from either side of the family when relevant.',
    'add-person',
  )
  if (planner.members.some((member) => member.relationship === 'Spouse / Partner'))
    card({
      id: 'parents-support',
      chapter: 'people',
      title: 'Do either of you support parents?',
      note: 'Think about both your parents and your partner’s parents.',
      illustration: 'family-support',
      required: true,
      rows: [['Support for parents', planner.parentsSupport]],
      validate: () => (!planner.parentsSupport ? 'Choose an answer to continue.' : ''),
      render: () => (
        <>
          <Options
            label='Parents to include'
            required
            value={planner.parentsSupport}
            onChange={(value) =>
              setPlanner((p) => ({
                ...p,
                parentsSupport: value,
                monthlyExpenses:
                  value === 'Neither' ? { ...p.monthlyExpenses, parents: '' } : p.monthlyExpenses,
              }))
            }
            options={['My parents', 'Partner’s parents', 'Both sides', 'Neither', 'Not sure']}
          />
          {['My parents', 'Partner’s parents', 'Both sides'].includes(planner.parentsSupport) && (
            <>
              <p className='q2-hint'>Add any parents who are not already included.</p>
              <button type='button' className='q2-add' onClick={() => addItem('members')}>
                ＋ Add a parent
              </button>
            </>
          )}
        </>
      ),
    })

  for (const income of planner.salaries)
    card({
      id: `salaries-${income.id}`,
      chapter: 'income',
      title: 'Tell us about this income.',
      note: 'Use a monthly average for income that varies. Enter the amount before deductions.',
      illustration: 'income-amount',
      required: true,
      rows: [
        ['Income earner', income.member || 'You'],
        ['Income type', income.incomeType],
        moneyRow('Monthly income', income.gross, ' / month'),
        ...(income.incomeType === 'Salary'
          ? ['tax', 'pf', 'nps'].map((key) => moneyRow(key.toUpperCase(), income[key], ' / month'))
          : []),
      ],
      validate: () =>
        !income.incomeType
          ? 'Choose an income type.'
          : !validAmount(income.gross)
          ? 'Add a monthly income amount. Enter zero if applicable.'
          : amountError(['tax', 'pf', 'nps'].map((key) => [income[key]])) ||
            (income.incomeType === 'Salary' &&
            ['tax', 'pf', 'nps'].reduce((sum, key) => sum + Number(income[key] || 0), 0) >
              Number(income.gross)
              ? 'Deductions cannot be more than this income.'
              : ''),
      render: () => (
        <>
          {itemField('salaries', income, 'member', 'Income earner', {
            options: ['You', ...planner.members.slice(1).map(personName)],
          })}
          {itemField('salaries', income, 'incomeType', 'Income type', {
            options: incomeTypes,
            required: true,
          })}
          {itemField('salaries', income, 'gross', 'Income before deductions', {
            type: 'money',
            unit: '/ month',
            required: true,
          })}
          {income.incomeType === 'Salary' && (
            <details className='q2-details'>
              <summary>
                Tax, PF & NPS deductions <span>Optional</span>
              </summary>
              <p className='q2-hint'>
                Add only deductions from this salary. Leaving these blank keeps the income you
                entered.
              </p>
              {['tax', 'pf', 'nps'].map((key) => (
                <React.Fragment key={key}>
                  {itemField(
                    'salaries',
                    income,
                    key,
                    key === 'tax' ? 'Income tax' : key.toUpperCase(),
                    { type: 'money', unit: '/ month' },
                  )}
                </React.Fragment>
              ))}
            </details>
          )}
        </>
      ),
    })
  collection(
    'salaries',
    'income',
    'Where does money come from?',
    'Include salary, business, rent or other regular income for everyone in this plan.',
    'add-income',
  )

  const housing = planner.monthlyExpenses.housing
  card({
    id: 'housing',
    chapter: 'spending',
    title: 'First, your housing costs.',
    note: 'Rent and a home-loan EMI are separate. You can have both.',
    illustration: 'rent-emi',
    required: true,
    rows: [
      ['Housing', housing],
      ...(['Rent', 'Both'].includes(housing)
        ? [moneyRow('Rent', planner.monthlyExpenses.rent, ' / month')]
        : []),
      ...(['Home-loan EMI', 'Both'].includes(housing)
        ? [moneyRow('Home-loan EMI', planner.monthlyExpenses.emi, ' / month')]
        : []),
    ],
    validate: () =>
      !housing
        ? 'Choose your housing situation.'
        : ['Rent', 'Both'].includes(housing) && !validAmount(planner.monthlyExpenses.rent)
        ? 'Add your monthly rent.'
        : ['Home-loan EMI', 'Both'].includes(housing) && !validAmount(planner.monthlyExpenses.emi)
        ? 'Add your monthly home-loan EMI.'
        : '',
    render: () => (
      <>
        {hasValue(planner.legacyHousingAmount) && (
          <p className='q2-callout'>
            Your earlier answer combined rent and EMI: {money(planner.legacyHousingAmount)}. Please
            separate that amount below.
          </p>
        )}
        <Options
          label='What do you pay?'
          required
          value={housing}
          onChange={(v) => setObject('monthlyExpenses', 'housing', v)}
          options={['Rent', 'Home-loan EMI', 'Both', 'Neither']}
        />
        {['Rent', 'Both'].includes(housing) &&
          field('monthlyExpenses', 'rent', 'Rent', {
            type: 'money',
            unit: '/ month',
            required: true,
          })}
        {['Home-loan EMI', 'Both'].includes(housing) &&
          field('monthlyExpenses', 'emi', 'Home-loan EMI', {
            type: 'money',
            unit: '/ month',
            required: true,
          })}
      </>
    ),
  })
  card({
    id: 'essentials',
    chapter: 'spending',
    title: 'What are your monthly essentials?',
    note: 'Food, utilities, phone, internet, transport and household help. Exclude rent, EMIs, school fees and family support asked separately.',
    illustration: 'monthly-home',
    required: true,
    rows: [moneyRow('Essential spending', planner.monthlyExpenses.household, ' / month')],
    validate: () =>
      !validAmount(planner.monthlyExpenses.household)
        ? 'Add your monthly essential spending. An estimate is fine.'
        : '',
    render: () =>
      field('monthlyExpenses', 'household', 'Essential monthly expenses', {
        type: 'money',
        unit: '/ month',
        required: true,
      }),
  })
  card({
    id: 'family-spending',
    chapter: 'spending',
    title: 'Any additional family support?',
    note: 'Only amounts not already included in your essentials. Exclude education fees—we’ll ask for those separately.',
    illustration: 'family-support',
    rows: [
      ...(planner.parentsSupport === 'Neither'
        ? []
        : [moneyRow('Parents', planner.monthlyExpenses.parents, ' / month')]),
      moneyRow('Children', planner.monthlyExpenses.children, ' / month'),
    ],
    render: () => (
      <>
        {planner.parentsSupport !== 'Neither' &&
          field('monthlyExpenses', 'parents', 'Parents (monthly)', {
            type: 'money',
            unit: '/ month',
            hint: 'Include parents from both sides, if applicable.',
          })}
        {field('monthlyExpenses', 'children', 'Children (monthly)', {
          type: 'money',
          unit: '/ month',
          hint: 'All your children together.',
        })}
      </>
    ),
  })
  card({
    id: 'annual-costs',
    chapter: 'spending',
    title: 'Which annual costs apply?',
    note: 'Select the relevant costs and add yearly estimates together. Leave an amount blank if you need to check it.',
    illustration: 'annual-cost',
    rows:
      planner.annualChoice === 'None'
        ? [['Annual expenses', 'None']]
        : selectedAnnualCosts.length
        ? selectedAnnualCosts.map(([key, label]) =>
            moneyRow(label, planner.yearlyExpenses[key], ' / year'),
          )
        : [['Annual expenses', 'Not answered']],
    render: () => (
      <>
        <div className='q2-checks'>
          {annualCosts.map(([key, label, hint]) => (
            <div key={key}>
              <label>
                <input
                  type='checkbox'
                  checked={planner.annualSelected.includes(key)}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setPlanner((p) => ({
                      ...p,
                      annualChoice: 'Selected',
                      annualSelected: checked
                        ? [...p.annualSelected, key]
                        : p.annualSelected.filter((value) => value !== key),
                    }))
                  }}
                />
                <span>{label}</span>
              </label>
              {planner.annualSelected.includes(key) &&
                field('yearlyExpenses', key, label, { type: 'money', unit: '/ year', hint })}
            </div>
          ))}
        </div>
        <button
          type='button'
          className={`q2-add ${planner.annualChoice === 'None' ? 'is-selected' : ''}`}
          aria-pressed={planner.annualChoice === 'None'}
          onClick={() => setPlanner((p) => ({ ...p, annualChoice: 'None', annualSelected: [] }))}
        >
          None of these apply
        </button>
      </>
    ),
  })
  const suggestedLifestyle = lifestyleEstimate(planner)
  card({
    id: 'lifestyle',
    chapter: 'spending',
    title: 'A little room for everything else.',
    note: 'An annual allowance for miscellaneous lifestyle expenses, beyond the costs already entered.',
    illustration: 'annual-cost',
    rows: [
      moneyRow('Miscellaneous lifestyle', planner.yearlyExpenses.lifestyle, ' / year'),
      [
        'Estimate status',
        planner.lifestyleConfirmed ? 'Confirmed by you' : 'Suggested · not confirmed',
      ],
    ],
    render: () => (
      <>
        <p className='q2-callout'>
          Suggested: <strong>{money(suggestedLifestyle)} / year</strong>
          <br />
          One month of essentials. Rent and EMI are excluded.
        </p>
        {field('yearlyExpenses', 'lifestyle', 'Miscellaneous lifestyle expenses', {
          type: 'money',
          unit: '/ year',
          hint: 'Adjust this to suit your actual spending.',
        })}
        <button
          type='button'
          className='q2-text-button'
          onClick={() => setObject('yearlyExpenses', 'lifestyle', String(suggestedLifestyle))}
        >
          Use suggested amount
        </button>
      </>
    ),
  })
  for (const cost of planner.extraYearlyExpenses)
    card({
      id: `extraYearlyExpenses-${cost.id}`,
      chapter: 'spending',
      title: 'Another annual cost.',
      note: 'Add a cost not already included above.',
      illustration: 'annual-cost',
      required: true,
      rows: [moneyRow(cost.name || 'Annual cost', cost.amount, ' / year')],
      validate: () =>
        !cost.name.trim() || !validAmount(cost.amount)
          ? 'Add the cost name and annual amount.'
          : '',
      render: () => (
        <>
          {itemField('extraYearlyExpenses', cost, 'name', 'Cost name', { required: true })}
          {itemField('extraYearlyExpenses', cost, 'amount', 'Annual amount', {
            type: 'money',
            unit: '/ year',
            required: true,
          })}
        </>
      ),
    })
  collection(
    'extraYearlyExpenses',
    'spending',
    'Anything else across the year?',
    'For example, maintenance or an annual family commitment not included above.',
    'add-annual-cost',
  )

  for (const holding of planner.holdings)
    card({
      id: `holdings-${holding.id}`,
      chapter: 'balance',
      title: 'What do you own?',
      note: 'Use the current value. You can check an exact amount later.',
      illustration: 'holding-value',
      required: true,
      rows: [
        ['Holding', holding.name],
        moneyRow('Current value', holding.value),
        ['Held with', holding.location],
      ],
      validate: () => (!holding.name ? 'Choose a holding type.' : ''),
      render: () => (
        <>
          {itemField('holdings', holding, 'name', 'Holding type', {
            options: assetTypes,
            required: true,
          })}
          {itemField('holdings', holding, 'value', 'Current value', {
            type: 'money',
            hint: 'Optional—an estimate is fine.',
          })}
          {itemField('holdings', holding, 'location', 'Where is it held?', {
            hint: 'Optional: bank, platform or location.',
          })}
        </>
      ),
    })
  collection(
    'holdings',
    'balance',
    'Bring your assets together.',
    'Add savings, investments and property relevant to this plan.',
    'add-holding',
  )
  const loanCards = planner.loans.filter(
    (loan) => isMeaningfulItem('loans', loan) || stepId === `loans-${loan.id}`,
  )
  for (const loan of loanCards)
    card({
      id: `loans-${loan.id}`,
      chapter: 'balance',
      title: 'Tell us about this loan.',
      note: 'This is the balance still owed, not your monthly repayment.',
      illustration: 'loan-value',
      required: true,
      rows: [
        ['Loan type', loan.type],
        moneyRow('Outstanding balance', loan.amount),
        moneyRow('Monthly EMI', loan.emi, ' / month'),
        ['Months left', hasValue(loan.monthsLeft) ? `${loan.monthsLeft} months` : 'Not answered'],
        ['Lender', loan.details],
      ],
      validate: () => {
        if (!loan.type) return 'Choose a loan type.'
        if (!hasValue(loan.amount)) return 'Add the outstanding balance.'
        if (!hasValue(loan.emi)) return 'Add the monthly EMI.'
        if (!hasValue(loan.monthsLeft)) return 'Add the months left to complete this loan.'
        if (amountError([[loan.amount], [loan.emi]])) return 'Enter valid loan amounts.'
        if (!Number.isFinite(Number(loan.monthsLeft)) || Number(loan.monthsLeft) <= 0)
          return 'Enter valid months left.'
        return ''
      },
      render: () => (
        <>
          {itemField('loans', loan, 'type', 'Loan type', {
            required: true,
            options: ['Home loan', 'Personal loan', 'Auto loan', 'Education loan', 'Other loan'],
          })}
          {loan.type === 'Home loan' && hasValue(planner.monthlyExpenses.emi) && (
            <p className='q2-callout'>
              You entered {money(planner.monthlyExpenses.emi)} / month in home-loan EMIs. Add the
              outstanding balance here; we won’t count the EMI again.
            </p>
          )}
          {itemField('loans', loan, 'amount', 'Outstanding balance', {
            type: 'money',
            required: true,
            hint: 'The balance still owed today.',
          })}
          {itemField('loans', loan, 'emi', 'Monthly EMI', {
            type: 'money',
            required: true,
            unit: '/ month',
            hint: 'The amount paid each month towards this loan.',
          })}
          {itemField('loans', loan, 'monthsLeft', 'Months left to complete', {
            type: 'number',
            required: true,
            hint: 'Approximate number of EMIs remaining.',
          })}
          {itemField('loans', loan, 'details', 'Lender', { hint: 'Optional' })}
        </>
      ),
    })
  collection(
    'loans',
    'balance',
    'What do you still owe?',
    'Include outstanding loans, monthly EMIs and the time left to repay them.',
    'add-loan',
  )

  const coveragePersonOptions = Array.from(new Set(planner.members.map(personName).filter(Boolean)))
  const coverageOptions = [...coveragePersonOptions, 'Multiple family members', 'Not sure']

  for (const [id, prefix, label, illustration] of [
    ['corporateMedical', 'corporate', 'Employer health insurance', 'medical-cover'],
    ['privateMedical', 'private', 'Personal health insurance', 'medical-cover'],
    ['term', 'term', 'Term life insurance', 'term-cover'],
  ]) {
    const answer = planner.insurance[id]
    card({
      id: `insurance-${id}`,
      chapter: 'protection',
      title:
        label === 'Term life insurance'
          ? 'Do you have term life cover?'
          : `Do you have ${prefix === 'corporate' ? 'employer' : 'personal'} health cover?`,
      note:
        prefix === 'term'
          ? 'Life insurance that pays your beneficiaries if you die during the policy term.'
          : 'The sum insured is your coverage limit, not the premium you pay.',
      illustration,
      rows: [
        [label, answer],
        ...(answer === 'Yes'
          ? [
              moneyRow('Sum insured', planner.insurance[`${prefix}Coverage`]),
              ['Who is covered', planner.insurance[`${prefix}Who`]],
              ...(prefix !== 'term'
                ? [['Coverage basis', planner.insurance[`${prefix}Basis`]]]
                : []),
            ]
          : []),
      ],
      render: () => (
        <>
          <Options label={label} value={answer} onChange={(v) => setObject('insurance', id, v)} />
          {answer === 'Yes' && (
            <>
              {field('insurance', `${prefix}Coverage`, 'Sum insured', {
                type: 'money',
                hint: 'Leave blank if you need to check your policy.',
              })}
              {field(
                'insurance',
                `${prefix}Who`,
                prefix === 'term' ? 'Whose life is insured?' : 'Who is covered?',
                { options: coverageOptions },
              )}
              {prefix !== 'term' &&
                field('insurance', `${prefix}Basis`, 'How is the cover shared?', {
                  options: ['Individual cover', 'Shared family cover', 'Not sure'],
                })}
            </>
          )}
        </>
      ),
    })
  }

  const children = planner.members
    .filter((member) => member.relationship === 'Child')
    .map((member) => ({ ...member, age: ageFromDob(member.dob) }))
    .sort((a, b) => (a.age ?? 999) - (b.age ?? 999))

  const childPlanSummary = children.length ? (
    <div className='q2-child-plan-summary'>
      {children.map((child) => {
        const plan = childPlanWithDefaults(
          planner.childPlans?.[child.id],
          child.id,
          planner,
          child.age,
        )
        const remainingStages = childEducationRemainingStages(child.age)
        return (
          <div className='q2-child-plan-card' key={child.id}>
            <div className='q2-child-plan-head'>
              <strong>{personName(child)}’s plan</strong>
              <button
                type='button'
                className='q2-child-plan-edit'
                onClick={() => transitionTo(`childSpecial-${child.id}`, 'forward')}
              >
                Edit
              </button>
            </div>
            {plan.specialNeeds ? (
              <p>Your advisor will get in touch about {personName(child)}.</p>
            ) : (
              <>
                <dl>
                  {remainingStages.map((stage) => (
                    <div key={stage.key}>
                      <dt>{stage.label}</dt>
                      <dd>{money(plan.stages?.[stage.key])} / year</dd>
                    </div>
                  ))}
                  {hasValue(plan.weddingToday) && (
                    <div>
                      <dt>Wedding at {plan.weddingAge || 27}</dt>
                      <dd>{money(plan.weddingToday)} today</dd>
                    </div>
                  )}
                </dl>
                {!hasValue(plan.weddingToday) && (
                  <p className='q2-hint'>Wedding planning skipped or not answered yet.</p>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  ) : null

  for (const goal of planner.assetGoals)
    card({
      id: `assetGoals-${goal.id}`,
      chapter: 'future',
      title: 'What would you like to make possible?',
      note: 'Start with a goal. Timing and amounts can be estimates, or added later.',
      illustration: 'goal-name',
      required: true,
      rows: [
        ['Goal', goal.name],
        ['Goal type', goal.kind || 'Other'],
        ['Target year', goal.year],
        moneyRow('Cost in today’s money', goal.amount),
        moneyRow('Already saved', goal.saved),
        ...(goal.kind === 'Education'
          ? [
              ['Child / student', goal.child],
              ['Study location', goal.studyLocation],
            ]
          : []),
      ],
      validate: () =>
        !goal.name.trim()
          ? 'Give this goal a name.'
          : hasValue(goal.year) &&
            (!/^\d{4}$/.test(String(goal.year)) ||
              Number(goal.year) < new Date().getFullYear() ||
              Number(goal.year) > new Date().getFullYear() + 100)
          ? 'Use a four-digit target year between this year and 100 years from now.'
          : '',
      render: () => (
        <>
          {children.length > 0 && goal.kind === 'Education' && (
            <>
              <p className='q2-callout'>
                Child education and wedding planning now has a dedicated screen. Use that planner
                for child-linked education costs.
              </p>
              <button
                type='button'
                className='q2-add'
                onClick={() => transitionTo(`childSpecial-${children[0].id}`, 'forward')}
              >
                Open child education planner
              </button>
            </>
          )}
          <Options
            label='Goal type'
            value={goal.kind || 'Other'}
            options={
              children.length && goal.kind !== 'Education'
                ? ['Retirement', 'Vacation', 'Home', 'Other']
                : ['Retirement', 'Education', 'Vacation', 'Home', 'Other']
            }
            onChange={(v) => setItem('assetGoals', goal.id, 'kind', v)}
          />
          {itemField('assetGoals', goal, 'name', 'Goal name', {
            required: true,
            hint: ['Buy a home', 'Home'].includes(goal.kind)
              ? 'For example: our first home in Bengaluru.'
              : 'Give it a name that means something to you.',
          })}
          {goal.kind === 'Education' && (
            <>
              {itemField('assetGoals', goal, 'child', 'Who is this education for?', {
                hint:
                  planner.members
                    .filter((m) => m.relationship === 'Child')
                    .map(personName)
                    .join(', ') || 'A child or another family member.',
              })}
              {itemField('assetGoals', goal, 'studyLocation', 'Where would they like to study?', {
                options: ['India', 'Abroad', 'Not decided yet'],
              })}
            </>
          )}
          {itemField(
            'assetGoals',
            goal,
            'year',
            goal.kind === 'Education' ? 'Study start year' : 'Target year',
            { type: 'year' },
          )}
          {itemField(
            'assetGoals',
            goal,
            'amount',
            ['Buy a home', 'Home'].includes(goal.kind)
              ? 'Estimated home budget today'
              : 'Estimated cost today',
            { type: 'money' },
          )}
          {itemField('assetGoals', goal, 'saved', 'Already saved towards this goal', {
            type: 'money',
            hint: 'Optional. If this is in your holdings, it is the same money—not an additional asset.',
          })}
        </>
      ),
    })
  collection(
    'assetGoals',
    'future',
    'What are you working towards?',
    children.length
      ? 'Education and wedding costs are planned separately for each child. Add a home, travel or a goal that is entirely your own here.'
      : 'A home, education, travel or a goal that is entirely your own.',
    'add-goal',
    childPlanSummary,
  )
  for (const [childIndex, child] of children.entries()) {
    const plan = childPlanWithDefaults(planner.childPlans?.[child.id], child.id, planner, child.age)
    const youngestPlan = children[0]
      ? childPlanWithDefaults(
          planner.childPlans?.[children[0].id],
          children[0].id,
          planner,
          children[0].age,
        )
      : null
    const remainingStages = childEducationRemainingStages(child.age)
    card({
      id: `childSpecial-${child.id}`,
      chapter: 'future',
      title: `Start ${personName(child)}’s child plan.`,
      note:
        childIndex === 0
          ? 'We start with the youngest child, then reuse those assumptions for older children if you choose.'
          : 'Copy the youngest child’s assumptions, then we calculate from this child’s current age.',
      illustration: 'school-fees',
      rows: plan.specialNeeds
        ? [['Special needs', 'Advisor follow-up requested']]
        : [
            ['Child age', child.age === null ? 'Date of birth needed' : `${child.age}`],
            [
              'Education stages left',
              remainingStages.map((stage) => stage.label).join(', ') || 'No education stages left',
            ],
          ],
      validate: () =>
        child.age === null
          ? `Add a valid date of birth for ${personName(child)} before planning child goals.`
          : '',
      render: () => (
        <>
          <div className='q2-checks q2-child-special'>
            <div>
              <label>
                <input
                  type='checkbox'
                  checked={Boolean(plan.specialNeeds)}
                  onChange={(e) =>
                    setChildPlan(child.id, (currentPlan) => ({
                      ...currentPlan,
                      specialNeeds: e.target.checked,
                    }))
                  }
                />
                <span>My child has special needs</span>
              </label>
            </div>
          </div>
          {plan.specialNeeds ? (
            <p className='q2-callout'>Your advisor will get in touch about {personName(child)}.</p>
          ) : (
            <>
              {childIndex > 0 && youngestPlan && (
                <button
                  type='button'
                  className='q2-add'
                  onClick={() =>
                    setChildPlan(child.id, (currentPlan) => ({
                      ...currentPlan,
                      educationInflation: youngestPlan.educationInflation,
                      growth: youngestPlan.growth,
                      studyLocation: youngestPlan.studyLocation,
                      weddingAge: youngestPlan.weddingAge,
                      weddingToday: youngestPlan.weddingToday,
                      weddingInflation: youngestPlan.weddingInflation,
                      stages: { ...youngestPlan.stages },
                    }))
                  }
                >
                  Copy youngest child’s plan
                </button>
              )}
              <p className='q2-callout'>
                {personName(child)} is {child.age}. We’ll only ask for stages that still have years
                remaining. Each amount should include incidentals like trips, uniforms and books.
              </p>
            </>
          )}
        </>
      ),
    })
    if (!plan.specialNeeds) {
      for (const stage of remainingStages) {
        const isCurrentStage = child.age >= stage.startAge && child.age < stage.endAge
        const plannedStartAge =
          child.age === null ? stage.startAge : Math.max(Math.floor(child.age) + 1, stage.startAge)
        card({
          id: `childStage-${child.id}-${stage.key}`,
          chapter: 'future',
          title: `${personName(child)}: ${stage.label} education costs.`,
          note:
            stage.key === 'school'
              ? 'Use current school fees if you entered them earlier; otherwise we use the default.'
              : 'This is an annual cost in today’s money. We project it forward from the child’s current age.',
          illustration: 'school-fees',
          rows: [
            ['Child age', `${child.age}`],
            ['Stage', stage.label],
            moneyRow('Amount per year', plan.stages?.[stage.key], ' / year'),
          ],
          validate: () => amountError([[plan.stages?.[stage.key]]]),
          render: () => (
            <>
              <p className='q2-callout'>
                {isCurrentStage
                  ? `${personName(
                      child,
                    )} is currently in this stage, so we only plan the years ahead.`
                  : `${personName(child)} reaches this stage at age ${stage.startAge}.`}{' '}
                This question covers ages {plannedStartAge} through {stage.endAge - 1}.
              </p>
              {stage.key === 'undergrad' && (
                <Options
                  label='Undergrad plan'
                  value={plan.studyLocation}
                  options={['India', 'Foreign']}
                  onChange={(value) =>
                    setChildPlan(child.id, (currentPlan) => ({
                      ...currentPlan,
                      studyLocation: value,
                      stages: {
                        ...currentPlan.stages,
                        undergrad: value === 'Foreign' ? '5000000' : '700000',
                      },
                    }))
                  }
                />
              )}
              {stage.key === 'grad' && (
                <Options
                  label='Postgrad plan'
                  value={plan.gradLocation}
                  options={['India', 'Foreign']}
                  onChange={(value) =>
                    setChildPlan(child.id, (currentPlan) => ({
                      ...currentPlan,
                      gradLocation: value,
                      stages: {
                        ...currentPlan.stages,
                        grad: value === 'Foreign' ? '10000000' : '1500000',
                      },
                    }))
                  }
                />
              )}
              <Field
                label={`${stage.label} amount per year`}
                type='money'
                unit='/ year'
                value={plan.stages?.[stage.key]}
                hint={
                  stage.key === 'undergrad'
                    ? 'India defaults to ₹7,00,000 per year. Foreign defaults to ₹50,00,000 per year.'
                    : stage.key === 'grad'
                    ? 'India defaults to ₹15,00,000 per year. Foreign defaults to ₹1,00,00,000 per year.'
                    : undefined
                }
                onChange={(value) =>
                  setChildPlan(child.id, (currentPlan) => ({
                    ...currentPlan,
                    stages: { ...currentPlan.stages, [stage.key]: value },
                  }))
                }
              />
            </>
          ),
        })
      }
      card({
        id: `childWedding-${child.id}`,
        chapter: 'future',
        title: `${personName(child)}: wedding costs.`,
        note: 'Enter the amount you would spend in today’s money. We project it forward separately.',
        illustration: 'goal-target',
        rows: [
          moneyRow('Wedding in today’s money', plan.weddingToday),
          ['Wedding age', plan.weddingAge],
        ],
        allowSkip: true,
        skipLabel: 'Skip wedding planning',
        validate: () => amountError([[plan.weddingToday], [plan.weddingAge]]),
        render: () => (
          <>
            <Field
              label='Wedding cost in today’s money'
              type='money'
              value={plan.weddingToday}
              onChange={(value) =>
                setChildPlan(child.id, (currentPlan) => ({
                  ...currentPlan,
                  weddingToday: value,
                }))
              }
            />
            <div className='q2-child-stage-grid'>
              <Field
                label='Wedding age'
                type='number'
                value={plan.weddingAge}
                onChange={(value) =>
                  setChildPlan(child.id, (currentPlan) => ({ ...currentPlan, weddingAge: value }))
                }
              />
            </div>
          </>
        ),
      })
    }
  }
  card({
    id: 'retirement',
    chapter: 'future',
    title: 'What should retirement feel like?',
    note: 'We’ll carry your monthly essentials, annual costs and holidays into retirement separately.',
    illustration: 'retirement-monthly',
    rows: [
      moneyRow('Monthly basic expenses', retirementModel.basicCurrent, ' / month'),
      moneyRow('Vacation', retirementModel.vacationCurrent, ' / year'),
      moneyRow('Other annual expenses', retirementModel.annualCurrent, ' / year'),
      [
        'Estimate status',
        planner.retirement.estimateConfirmed ? 'Confirmed by you' : 'Not confirmed',
      ],
    ],
    render: () => (
      <>
        <p className='q2-callout'>
          Each amount starts from the spending you have already entered. Inflation and suggested
          reserves are included automatically in your estimate.
        </p>
        {field('retirement', 'basicCurrent', 'Monthly basic expenses', {
          type: 'money',
          unit: '/ month',
          hint: `Suggested from essentials and rent: ${money(
            retirementModel.sourceBasicExpense,
          )} / month.`,
        })}

        {field('retirement', 'vacation', 'Vacation', {
          type: 'money',
          unit: '/ year',
          hint: `Suggested from travel and holidays: ${money(
            retirementModel.sourceVacationExpense,
          )} / year.`,
        })}

        {field('retirement', 'annual', 'Other annual expenses', {
          type: 'money',
          unit: '/ year',
          hint: `Suggested from insurance, school fees and lifestyle costs: ${money(
            retirementModel.sourceAnnualExpense,
          )} / year.`,
        })}
      </>
    ),
  })

  const cards = CHAPTERS.flatMap(([chapter, title, intro, note]) => [
    { id: `intro-${chapter}`, chapter, title: intro, note, intro: true, label: title },
    ...raw
      .filter((item) => item.chapter === chapter)
      .sort((a, b) => {
        if (chapter !== 'future') return 0
        const priority = (item) => {
          if (
            item.id.startsWith('childSpecial-') ||
            item.id.startsWith('childStage-') ||
            item.id.startsWith('childWedding-')
          )
            return 0
          if (item.id === 'retirement') return 1
          return 2
        }
        return priority(a) - priority(b)
      }),
  ])
  const welcome = {
    id: 'welcome',
    intro: true,
    title: 'Your life. Your priorities. Your plan.',
    note: 'Let’s build your financial picture, one chapter at a time.',
    label: 'Before we begin',
  }
  const reviewIntro = {
    id: 'intro-review',
    intro: true,
    title: 'Let’s put it all together.',
    note: 'Check the picture, change any answer and spot anything you want to come back to.',
    label: 'Your review',
  }
  const allCards = [
    welcome,
    ...cards,
    reviewIntro,
    { id: 'review', title: 'Your financial picture.', label: 'Review', review: true },
  ]
  const current = allCards.find((item) => item.id === stepId) || welcome
  const position = allCards.findIndex((item) => item.id === current.id)
  const chapterIndex = CHAPTERS.findIndex(([id]) => id === current.chapter)
  const answeredRows = current.rows || []
  const optionalHasAnswer = answeredRows.some(
    ([, value]) =>
      hasValue(value) &&
      !['Not answered', 'Not confirmed', 'Suggested · not confirmed'].includes(value),
  )

  useEffect(() => {
    let active = true
    setLoadFailed(false)
    const details = customerProfile || getUserDetails() || {}
    const userPhone = details.phoneNumber || details.phone_number
    setProfile(details)
    const initial = createPlanner()
    initial.members[0].name =
      [details.firstName, details.lastName].filter(Boolean).join(' ') || details.name || ''
    const local = () => {
      try {
        const draft = JSON.parse(localStorage.getItem(LOCAL_DRAFT_KEY) || 'null')
        return draft?.owner === (userPhone || 'local') ? draft : null
      } catch {
        return null
      }
    }
    const restore = (saved) => {
      const source = saved?.planner || saved?.userResponse?.find((item) => item?.planner)?.planner
      if (source?.schemaVersion !== 3) return false
      const restored = restorePlanner(source)
      if (!restored.members[0].name) restored.members[0].name = initial.members[0].name
      setPlanner(restored)
      setStepId(restored.questionnaire2Step || 'welcome')
      setCompleted(Boolean(saved.isCompleted && source.questionnaire2Version === 2))
      setShowEstimate(false)
      return true
    }
    async function load() {
      try {
        if (!userPhone) {
          if (!isLocal || !restore(local())) setPlanner(initial)
          return
        }
        const result = await get(`${env.API_URL}${API_PATH}`, {}, { phoneNumber: userPhone })
        if (!active) return
        if (result?.isError) {
          if (customerProfile && result.status === 404) {
            setPlanner(initial)
            return
          }
          if (customerProfile) {
            setLoadFailed(true)
            return
          }
          if (!isLocal || !restore(local())) setPlanner(initial)
        } else if (!restore(result)) {
          setPlanner(initial)
          if (isLocal) localStorage.removeItem(LOCAL_DRAFT_KEY)
        }
      } catch {
        if (active && customerProfile) setLoadFailed(true)
        else if (active && (!isLocal || !restore(local()))) setPlanner(initial)
      } finally {
        if (active) setHydrated(true)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [customerProfile])
  useEffect(() => {
    setError('')
    setRemoveId('')
    heading.current?.focus({ preventScroll: true })
    heading.current?.closest('main')?.scrollIntoView({ block: 'start', behavior: 'instant' })
  }, [stepId])
  useEffect(() => {
    if (stepId === 'lifestyle' && !hasValue(planner.yearlyExpenses.lifestyle))
      setObject('yearlyExpenses', 'lifestyle', String(lifestyleEstimate(planner)))
  }, [stepId])
  useEffect(() => {
    if (stepId !== 'retirement') return
    setPlanner((current) => {
      const source = retirementStreams(current)
      const retirement = current.retirement || {}
      const nextRetirement = {
        ...retirement,
        basicCurrent:
          hasValue(retirement.basicCurrent) || !source.sourceBasicExpense
            ? retirement.basicCurrent
            : String(source.sourceBasicExpense),
        vacation:
          hasValue(retirement.vacation) || !source.sourceVacationExpense
            ? retirement.vacation
            : String(source.sourceVacationExpense),
        annual:
          hasValue(retirement.annual) || !source.sourceAnnualExpense
            ? retirement.annual
            : String(source.sourceAnnualExpense),
      }
      if (
        nextRetirement.basicCurrent === retirement.basicCurrent &&
        nextRetirement.vacation === retirement.vacation &&
        nextRetirement.annual === retirement.annual
      )
        return current
      return { ...current, retirement: nextRetirement }
    })
  }, [stepId])

  async function save(nextId, snapshot = planner, finish = false) {
    if (saveLock.current) return false
    saveLock.current = true
    setSaving(true)
    setError('')
    const persisted = {
      ...cleanPlanner(snapshot),
      version: 2,
      schemaVersion: 3,
      questionnaire2Version: 2,
      questionnaire2Step: nextId,
    }
    try {
      if (phone) {
        const result = await post(`${env.API_URL}${API_PATH}`, {
          phoneNumber: phone,
          userResponse: [{ planner: persisted }],
          isCompleted: finish,
        })
        if (result?.isError) throw new Error('save')
      } else if (!isLocal) throw new Error('session')
      if (isLocal) {
        try {
          localStorage.setItem(
            LOCAL_DRAFT_KEY,
            JSON.stringify({ planner: persisted, isCompleted: finish, owner: phone || 'local' }),
          )
        } catch {
          if (!phone) throw new Error('storage')
        }
      }
      savedSignature.current = JSON.stringify(snapshot)
      setSavedLabel(phone ? 'Saved' : 'Saved on this device')
      return true
    } catch {
      setError('Your answers could not be saved. They are still on this screen. Please try again.')
      return false
    } finally {
      saveLock.current = false
      setSaving(false)
    }
  }
  function validate(item) {
    const custom = item.validate?.()
    if (custom) return custom
    // Validate numeric fields even on optional cards; leaving them blank is always allowed.
    const inputs = document.querySelectorAll('.q2-money input')
    return [...inputs].some((input) => input.value && !validAmount(input.value.replace(/,/g, '')))
      ? 'Enter valid amounts of zero or more.'
      : ''
  }
  // Hold the outgoing screen until its exit finishes, then mount the incoming one.
  async function transitionTo(nextId, direction = 'forward', commit) {
    if (transitionLock.current || nextId === current.id) return
    transitionLock.current = true
    setTransitioning(true)
    const panel = panelRef.current
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    try {
      if (panel && !reduced) {
        const targets = current.intro ? [...panel.children] : [panel]
        await Promise.all(
          targets.map((target) =>
            target
              .animate(
                current.intro
                  ? [
                      { opacity: 1, transform: 'translateY(0)' },
                      { opacity: 0, transform: 'translateY(-18px)' },
                    ]
                  : [
                      { opacity: 1, transform: 'translateX(0)' },
                      { opacity: 0, transform: `translateX(${direction === 'back' ? 28 : -28}px)` },
                    ],
                {
                  duration: current.intro ? 180 : 170,
                  easing: 'cubic-bezier(.4,0,1,1)',
                  fill: 'forwards',
                },
              )
              .finished.catch(() => {}),
          ),
        )
      }
      setMotion({ direction, fromIntro: Boolean(current.intro) })
      commit?.()
      setStepId(nextId)
    } finally {
      transitionLock.current = false
      setTransitioning(false)
    }
  }
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    const measure = () =>
      panel.style.setProperty(
        '--q2-header-height',
        `${panel.querySelector('.q2-hero')?.offsetHeight || 160}px`,
      )
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(panel)
    return () => observer.disconnect()
  }, [stepId, hydrated, completed])

  async function advance(later = false) {
    if (!later) {
      const message = validate(current)
      if (message) {
        setError(message)
        return
      }
    }
    let snapshot = {
      ...planner,
      answers: { ...planner.answers, [current.id]: later ? 'later' : 'done' },
    }
    if (current.id === 'lifestyle') snapshot.lifestyleConfirmed = !later
    if (current.id === 'retirement')
      snapshot.retirement = {
        ...snapshot.retirement,
        estimateConfirmed:
          !later &&
          (hasValue(snapshot.retirement.basicCurrent) ||
            hasValue(snapshot.retirement.monthly) ||
            retirementModel.sourceBasicExpense > 0),
      }
    const nextId = editing?.returnTo || allCards[position + 1]?.id || 'review'
    if (editing) {
      await transitionTo(nextId, 'forward', () => {
        setPlanner(snapshot)
        setEditing(editing.parent || null)
      })
      if (!editing.parent) save(nextId, snapshot)
      return
    }
    if (await save(nextId, snapshot)) {
      await transitionTo(nextId, 'forward', () => {
        setPlanner(snapshot)
      })
    }
  }
  function cancelEdit() {
    transitionTo(editing.returnTo, 'back', () => {
      setPlanner(editing.before)
      setEditing(editing.parent || null)
    })
  }
  async function finish() {
    const invalid = raw.find((item) => item.required && item.validate?.())
    if (invalid) {
      setError(`Please complete “${invalid.title}” before saving your profile.`)
      return
    }
    if (transitionLock.current) return
    const profile = { ...planner, loans: activeLoans }
    if (await save('review', profile, true)) {
      setTransitioning(true)
      transitionLock.current = true
      let expansion
      let restoreCard
      try {
        const header = panelRef.current?.querySelector('.q2-hero')
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (header && !reduced) {
          // Contract the long review while following it upward from the save button.
          const panel = panelRef.current
          let scroller = panel.parentElement
          while (
            scroller &&
            !(
              scroller.scrollHeight > scroller.clientHeight &&
              /auto|scroll/.test(getComputedStyle(scroller).overflowY)
            )
          ) {
            scroller = scroller.parentElement
          }
          scroller = scroller || document.scrollingElement
          const rootScroll = scroller === document.scrollingElement
          const startScroll = scroller.scrollTop
          const startHeight = panel.getBoundingClientRect().height
          const endHeight = header.getBoundingClientRect().height
          const scrollFrameTop = rootScroll ? 0 : scroller.getBoundingClientRect().top
          const endScroll = Math.max(
            0,
            startScroll + panel.getBoundingClientRect().top - scrollFrameTop - 84,
          )
          const originalStyle = panel.getAttribute('style')
          const originalBehavior = scroller.style.scrollBehavior
          const originalAnchor = scroller.style.overflowAnchor
          scroller.style.scrollBehavior = 'auto'
          scroller.style.overflowAnchor = 'none'
          panel.style.overflow = 'hidden'
          panel.style.height = `${startHeight}px`
          const fades = [...panel.children]
            .filter((child) => child !== header)
            .map((child) =>
              child.animate(
                [
                  { opacity: 1, transform: 'translateY(0)' },
                  { opacity: 0, transform: 'translateY(-24px)' },
                ],
                { duration: 400, easing: 'ease-in', fill: 'forwards' },
              ),
            )
          restoreCard = () => {
            fades.forEach((animation) => animation.cancel())
            if (originalStyle === null) panel.removeAttribute('style')
            else panel.setAttribute('style', originalStyle)
            scroller.style.scrollBehavior = originalBehavior
            scroller.style.overflowAnchor = originalAnchor
          }
          await new Promise((resolve) => {
            const start = performance.now()
            function contract(now) {
              const progress = Math.min(1, (now - start) / 1100)
              const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2
              panel.style.height = `${startHeight + (endHeight - startHeight) * eased}px`
              scroller.scrollTop = startScroll + (endScroll - startScroll) * eased
              if (progress < 1) requestAnimationFrame(contract)
              else resolve()
            }
            requestAnimationFrame(contract)
          })
          await new Promise((resolve) => requestAnimationFrame(resolve))
          const bounds = header.getBoundingClientRect()
          expansion = header.cloneNode(true)
          expansion.classList.add('q2-finale-expansion')
          expansion.setAttribute('aria-hidden', 'true')
          expansion.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'))
          Object.assign(expansion.style, {
            position: 'fixed',
            left: `${bounds.left}px`,
            top: `${bounds.top}px`,
            width: `${bounds.width}px`,
            height: `${bounds.height}px`,
            margin: '0',
            minHeight: '0',
            zIndex: '200',
            pointerEvents: 'none',
            boxSizing: 'border-box',
            borderRadius: '14px 14px 0 0',
          })
          document.body.appendChild(expansion)
          expansion.firstElementChild?.animate(
            [{ opacity: 1 }, { opacity: 0, transform: 'translateY(-16px)' }],
            { duration: 220, easing: 'ease-out', fill: 'forwards' },
          )
          await expansion.animate(
            [
              {
                left: `${bounds.left}px`,
                top: `${bounds.top}px`,
                width: `${bounds.width}px`,
                height: `${bounds.height}px`,
                borderRadius: '14px 14px 0 0',
              },
              {
                left: '0px',
                top: '0px',
                width: `${window.innerWidth}px`,
                height: `${window.innerHeight}px`,
                borderRadius: '0px',
              },
            ],
            { duration: 950, easing: 'cubic-bezier(.76,0,.24,1)', fill: 'forwards' },
          ).finished
        }
        setCelebrate(true)
        setPlanner(profile)
        setCompleted(true)
        // Keep the expanded grid in place until the finale has painted beneath it.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      } finally {
        expansion?.remove()
        restoreCard?.()
        transitionLock.current = false
        setTransitioning(false)
      }
    }
  }
  async function exit() {
    if (await save(current.id)) navigate(returnPath)
  }
  function back() {
    if (editing) cancelEdit()
    else if (position > 0) transitionTo(allCards[position - 1].id, 'back')
    else exit()
  }
  const review = () => (
    <div className='q2-review'>
      <p>
        Review each chapter. “Not answered” and “Answer later” are different from zero or none. You
        can save a first draft with optional details still to add.
      </p>
      {CHAPTERS.map(([chapter, title], index) => (
        <section className='q2-review-chapter' key={chapter}>
          <h2>
            <span>{String(index + 1).padStart(2, '0')}</span>
            {title}
          </h2>
          {raw
            .filter((item) => item.chapter === chapter)
            .map((item) =>
              item.collection ? (
                <div className='q2-review-manage' key={item.id}>
                  {!item.hasItems && (
                    <span>No {collectionNames[item.collectionSection]} added</span>
                  )}
                  <button
                    type='button'
                    onClick={() => editItem(item.id, 'review')}
                    aria-label={`Manage ${collectionNames[item.collectionSection]}`}
                  >
                    Manage
                  </button>
                </div>
              ) : (
                <div className='q2-review-entry' key={item.id}>
                  <div>
                    <h3>
                      {item.collection
                        ? `Manage ${collectionNames[item.collectionSection]}`
                        : item.title}
                    </h3>
                    <button
                      type='button'
                      onClick={() => editItem(item.id, 'review')}
                      aria-label={
                        item.collection
                          ? `Manage ${collectionNames[item.collectionSection]}`
                          : `Edit ${item.title}`
                      }
                    >
                      {item.collection ? 'Manage' : 'Edit'}
                    </button>
                  </div>
                  {planner.answers[item.id] === 'later' && (
                    <span className='q2-status'>Answer later</span>
                  )}
                  {item.required && item.validate?.() && (
                    <span className='q2-status'>Required details missing</span>
                  )}
                  <dl>
                    {(!item.collection || !item.hasItems) &&
                      item.rows?.map(([label, value], i) => (
                        <div key={`${label}-${i}`}>
                          <dt>{label}</dt>
                          <dd
                            className={
                              !hasValue(value) || value === 'Not answered' ? 'q2-unanswered' : ''
                            }
                          >
                            {hasValue(value) ? value : 'Not answered'}
                          </dd>
                        </div>
                      ))}
                  </dl>
                </div>
              ),
            )}
        </section>
      ))}
    </div>
  )

  if (loadFailed)
    return (
      <div className='q2-page'>
        <p role='alert'>
          Unable to load this customer’s saved answers. Please retry before editing.
        </p>
        <button type='button' className='q2-primary' onClick={() => window.location.reload()}>
          Retry
        </button>
        <button type='button' className='q2-text-button' onClick={() => navigate(returnPath)}>
          Back to First Ideas
        </button>
      </div>
    )
  if (!hydrated)
    return (
      <div className='fabits-questionnaire-page q2-page'>
        <p role='status'>Opening your financial picture…</p>
      </div>
    )
  if (completed)
    if (showEstimate)
      return (
        <QuestionnaireEstimate
          planner={planner}
          origin={estimateOrigin}
          adminMode={Boolean(customerProfile)}
          onBack={() => {
            setCelebrate(false)
            setShowEstimate(false)
          }}
          onReview={() => {
            setCelebrate(false)
            setCompleted(false)
            setShowEstimate(false)
            setStepId('review')
          }}
          onDashboard={() => navigate(returnPath)}
        />
      )
  if (completed)
    return (
      <QuestionnaireCelebration
        firstName={firstName}
        celebrate={celebrate}
        onEstimate={(origin) => {
          setEstimateOrigin(origin)
          setShowEstimate(true)
        }}
        onReview={() => {
          setCelebrate(false)
          setCompleted(false)
          setShowEstimate(false)
          setStepId('review')
        }}
      />
    )
  return (
    <div className='fabits-questionnaire-page q2-page'>
      <main className='fabits-questionnaire-shell'>
        <header className='q2-topbar'>
          <button
            type='button'
            className='fabits-questionnaire-icon-button'
            onClick={back}
            disabled={saving || transitioning}
            aria-label={position === 0 ? 'Save and exit' : 'Go back'}
          >
            {position === 0 ? <CloseIcon /> : <ChevronLeftIcon />}
          </button>
          <span>
            {current.label || CHAPTERS[chapterIndex]?.[1]}
            {chapterIndex >= 0 && (
              <small>
                Chapter {chapterIndex + 1} of {CHAPTERS.length}
              </small>
            )}
          </span>
          <button
            type='button'
            className='q2-text-button'
            onClick={exit}
            disabled={saving || transitioning}
          >
            Save & exit
          </button>
        </header>
        <nav className='q2-progress' aria-label='Chapter progress'>
          {CHAPTERS.map(([id, title], index) => (
            <span
              key={id}
              title={title}
              aria-label={`${title}${
                index < chapterIndex || current.id.includes('review')
                  ? ', visited'
                  : index === chapterIndex
                  ? ', current'
                  : ', upcoming'
              }`}
              className={
                current.id.includes('review') || index < chapterIndex
                  ? 'is-done'
                  : index === chapterIndex
                  ? 'is-current'
                  : ''
              }
            />
          ))}
        </nav>
        <div className='fabits-questionnaire-card-stage'>
          <section
            key={current.id}
            ref={panelRef}
            aria-busy={transitioning}
            className={`fabits-questionnaire-panel q2-panel ${
              current.intro ? 'q2-intro' : 'q2-question'
            } ${current.review ? 'q2-review-panel' : ''} q2-enter-${motion.direction} ${
              motion.fromIntro && !current.intro ? 'q2-from-intro' : ''
            }`}
          >
            <div className='fabits-questionnaire-hero q2-hero'>
              <div>
                <p className='fabits-questionnaire-eyebrow'>
                  {current.label || CHAPTERS[chapterIndex]?.[1]}
                </p>
                <h1 ref={heading} tabIndex={-1}>
                  {current.title}
                </h1>
                {current.note && <p>{current.note}</p>}
              </div>
            </div>
            {current.intro ? (
              <div className='q2-intro-body'>
                {current.id === 'welcome' ? (
                  <>
                    <p className='q2-intro-lede'>
                      We’ll use six parts of your life to shape a useful starting point.
                    </p>
                    <ol>
                      {CHAPTERS.map(([, title], i) => (
                        <li key={title}>
                          <span>{String(i + 1).padStart(2, '0')}</span>
                          {title}
                        </li>
                      ))}
                    </ol>
                    <p>
                      Estimates are welcome. Save and return at any time, then review your answers
                      before finishing.
                    </p>
                  </>
                ) : (
                  <>
                    <span className='q2-intro-number' aria-hidden='true'>
                      {current.id === 'intro-review'
                        ? '✓'
                        : String(chapterIndex + 1).padStart(2, '0')}
                    </span>
                    <p>
                      {current.id === 'intro-review'
                        ? 'Your answers stay editable. Suggested amounts are clearly marked, so you can make them your own.'
                        : 'One part of the picture. A few thoughtful answers.'}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                {!current.review && (
                  <div className='fabits-questionnaire-visual fabits-questionnaire2-visual'>
                    <img src={icon(current.illustration || 'plan-assumptions')} alt='' />
                  </div>
                )}
                <div className='q2-content'>
                  {current.review ? (
                    review()
                  ) : (
                    <fieldset disabled={saving || transitioning}>{current.render()}</fieldset>
                  )}
                </div>
              </>
            )}
            <footer className='q2-actions'>
              {error && (
                <p className='q2-error' role='alert'>
                  {error}
                </p>
              )}
              <button
                type='button'
                className='q2-primary'
                disabled={saving || transitioning}
                onClick={() => (current.review ? finish() : advance())}
              >
                {saving
                  ? 'Saving…'
                  : current.review
                  ? 'Save my financial profile'
                  : editing
                  ? 'Save changes'
                  : current.id === 'welcome'
                  ? 'Let’s begin'
                  : current.id === 'intro-review'
                  ? 'Review my answers'
                  : current.intro
                  ? 'Start this chapter'
                  : current.action || 'Continue'}
              </button>
              {editing ? (
                <button
                  type='button'
                  className='q2-text-button'
                  disabled={saving || transitioning}
                  onClick={cancelEdit}
                >
                  Cancel changes
                </button>
              ) : (
                !current.intro &&
                !current.required &&
                !current.collection &&
                !current.review &&
                (current.allowSkip || !optionalHasAnswer) && (
                  <button
                    type='button'
                    className='q2-text-button'
                    disabled={saving || transitioning}
                    onClick={() => advance(true)}
                  >
                    {current.skipLabel || 'Answer later'}
                  </button>
                )
              )}
              <div className='q2-card-meta'>
                <span aria-live='polite'>
                  {(savedSignature.current === JSON.stringify(planner) && savedLabel) ||
                    (current.required
                      ? 'Required details are marked above'
                      : 'You can review every answer')}
                </span>
                {!current.intro && !current.review && (
                  <span
                    aria-label={`Question ${raw.findIndex((item) => item.id === current.id) + 1}`}
                  >
                    {String(raw.findIndex((item) => item.id === current.id) + 1).padStart(2, '0')}
                  </span>
                )}
              </div>
            </footer>
          </section>
        </div>
      </main>
    </div>
  )
}
